import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import DecryptedText from '../components/DecryptedText'
import ScrollVelocity from '../components/ScrollVelocity'

export function Hero() {
  const badgeRef = useRef<HTMLDivElement>(null)
  const isLive = import.meta.env.VITE_APPSTORE_LIVE === '1'
  const storeUrl = import.meta.env.VITE_APPSTORE_URL || ''
  const storeDirect = isLive && !!storeUrl

  useEffect(() => {
    const el = badgeRef.current
    if (!el) return

    // Honour prefers-reduced-motion — keep the static -3deg tilt, no scroll wobble.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (reduce.matches) return

    const setRot = gsap.quickTo(el, 'rotation', { duration: 0.55, ease: 'power3.out' })

    let last = window.scrollY
    let lastTime = performance.now()
    let smoothed = 0
    let raf = 0
    let hovered = false
    let visible = true

    const tick = (now: number) => {
      const dt = Math.max(1, now - lastTime) / 1000
      lastTime = now
      const cur = window.scrollY
      const rawVel = (cur - last) / dt
      last = cur
      if (!hovered) {
        smoothed = smoothed * 0.85 + rawVel * 0.15
        const rot = Math.max(-8, Math.min(8, smoothed * 0.012))
        setRot(-3 + rot)
      }
      raf = requestAnimationFrame(tick)
    }

    const startLoop = () => {
      if (raf) return
      last = window.scrollY
      lastTime = performance.now()
      raf = requestAnimationFrame(tick)
    }
    const stopLoop = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = 0
    }

    // Pause RAF when badge is offscreen — saves battery + idle CPU on iOS.
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting
        if (visible) startLoop()
        else stopLoop()
      },
      { threshold: 0 }
    )
    io.observe(el)
    if (visible) startLoop()

    const onEnter = () => {
      hovered = true
      gsap.to(el, {
        rotation: 6,
        scale: 1.05,
        duration: 0.65,
        ease: 'elastic.out(1, 0.45)',
        overwrite: 'auto',
      })
    }
    const onLeave = () => {
      gsap.to(el, {
        rotation: -3,
        scale: 1,
        duration: 0.5,
        ease: 'expo.out',
        overwrite: 'auto',
        onComplete: () => {
          hovered = false
        },
      })
    }
    el.addEventListener('pointerenter', onEnter)
    el.addEventListener('pointerleave', onLeave)
    el.addEventListener('focus', onEnter)
    el.addEventListener('blur', onLeave)

    return () => {
      stopLoop()
      io.disconnect()
      el.removeEventListener('pointerenter', onEnter)
      el.removeEventListener('pointerleave', onLeave)
      el.removeEventListener('focus', onEnter)
      el.removeEventListener('blur', onLeave)
    }
  }, [])

  return (
    <section
      id="top"
      aria-label="Introduction"
      className="relative min-h-[100svh] flex flex-col items-center justify-center pt-28 sm:pt-32 pb-12 overflow-hidden"
    >
      {/* H1 lives below as a visible headline (was sr-only — page read like a
          screensaver on mobile). Marquee above is decorative, aria-hidden. */}

      {/* Ambient bg — radial-gradient avoids the GPU cost of filter:blur(180px)
          which was the most expensive paint on iOS Safari at hero. */}
      <div aria-hidden className="absolute inset-0 -z-10">
        <div
          className="absolute -top-72 left-1/2 -translate-x-1/2 w-[800px] max-w-[140vw] h-[600px] rounded-full opacity-40"
          style={{ background: 'radial-gradient(closest-side, var(--accent-soft) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-72 right-0 w-[600px] max-w-[120vw] h-[600px] rounded-full opacity-30"
          style={{ background: 'radial-gradient(closest-side, var(--accent-soft) 0%, transparent 70%)' }}
        />
      </div>

      {/* Status badge — wobbles with scroll velocity (disabled under prefers-reduced-motion) */}
      <div className="relative z-10 mb-6 md:mb-10 flex justify-center px-4">
        <div
          ref={badgeRef}
          tabIndex={0}
          className="hero-badge inline-flex items-center gap-2 sm:gap-2.5 pl-1.5 pr-3 sm:pl-2 sm:pr-4 py-1.5 rounded-full will-change-transform max-w-full cursor-default"
          style={{ transform: 'rotate(-3deg)' }}
          role="status"
        >
          <span className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 rounded-full bg-[var(--accent)] text-bg-0 text-[10px] font-bold uppercase tracking-[2px]">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-bg-0 animate-pulse" />
            Live
          </span>
          <span className="text-[12px] sm:text-[13px] text-ink-1 font-medium whitespace-nowrap">
            On the App Store
          </span>
          <span aria-hidden className="hidden xs:inline text-ink-3">·</span>
          <span className="hidden xs:inline text-[12px] sm:text-[13px] text-ink-2 whitespace-nowrap">
            Free · iOS 26
          </span>
        </div>
      </div>

      {/* Decorative wordmark marquee — hidden from AT. Letters render as a
          translucent fill, the separator dot keeps the original outlined
          treatment so it reads as a punctuation mark, not as a glyph. */}
      <div className="w-full" style={{ contain: 'paint' as never }} aria-hidden>
        <ScrollVelocity
          texts={[
            <>
              Ruvi Cash <span className="hero-marquee-dot">·</span>{' '}
            </>,
          ]}
          velocity={50}
          damping={0.92}
          velocityMultiplier={3}
          numCopies={5}
          className="hero-marquee"
        />
      </div>

      <div className="mt-4 md:mt-6 px-6 md:px-16 max-w-[820px] text-center">
        <h1 className="text-[15px] md:text-[17px] text-ink-2 leading-relaxed text-balance font-normal">
          <DecryptedText
            text="Money habits that actually stick. Tap-to-pay auto-import, 35 currencies, streaks and on-device privacy — built natively for iOS 26."
            animateOn="view"
            sequential
            revealDirection="start"
            speed={22}
            className="text-ink-2"
            encryptedClassName="text-ink-3 opacity-70"
          />
        </h1>
      </div>

      {/* Mobile-only CTA — the header's App Store pill is hidden below md,
          so surface the same action here for phones. */}
      <a
        {...(storeDirect
          ? { href: storeUrl, target: '_blank' as const, rel: 'noopener noreferrer' as const }
          : { href: '/#beta' })}
        className="mt-7 md:hidden inline-flex items-center h-[52px] px-7 rounded-full bg-[var(--accent)] text-bg-0 text-[15px] font-semibold shadow-[0_10px_36px_-10px_var(--accent-ring)]"
      >
        Get it on the App Store
      </a>
    </section>
  )
}
